import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, BadgeCheck, Car, Bike, Shield, Users, UserCheck, KeyRound } from "lucide-react";
import VehiclesManager from "@/components/VehiclesManager";
import GuardsManager from "@/components/GuardsManager";
import PendingResidentsManager from "@/components/PendingResidentsManager";
import TestAdminsManager from "@/components/TestAdminsManager";
import { parseVehicles, formatVehiclesForPayload, vehicleSearchString } from "@/lib/vehicles";

const EMPTY = { tower: "", flat_no: "", owner_name: "", tenant_name: "", phone: "", email: "", occupancy: "owner", vehicles: [], notes: "" };

function FlatForm({ open, initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || EMPTY);
    const [vehicles, setVehicles] = useState([]);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setForm(initial || EMPTY);
        setVehicles(parseVehicles(initial?.vehicles));
    }, [initial]);
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                vehicles: formatVehiclesForPayload(vehicles),
            };
            let res;
            if (initial?.flat_id) res = await api.patch(`/flats/${initial.flat_id}`, payload);
            else res = await api.post("/flats", payload);
            toast.success(initial?.flat_id ? "Flat updated" : "Flat added");
            onSaved(res.data);
            onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl border border-nest-line w-full max-w-lg p-6 lg:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="flat-form-modal">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="nest-stat-label">{initial?.flat_id ? "Edit" : "New"}</p>
                        <h3 className="font-display text-2xl mt-1 tracking-tight">{initial?.flat_id ? "Update flat record" : "Add a flat to directory"}</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-nest-alt"><X size={16}/></button>
                </div>
                <form onSubmit={submit} className="mt-5 grid gap-4">
                    <div className="grid grid-cols-2 gap-3">
                        <input required placeholder="Tower" value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-tower"/>
                        <input required placeholder="Flat no." value={form.flat_no} onChange={(e) => setForm({ ...form, flat_no: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-no"/>
                    </div>
                    <input placeholder="Owner name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-owner"/>
                    <input placeholder="Tenant name (if rented)" value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-tenant"/>
                    <div className="grid grid-cols-2 gap-3">
                        <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-phone"/>
                        <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-email"/>
                    </div>
                    <div className="pt-1">
                        <VehiclesManager vehicles={vehicles} onChange={setVehicles} label="Flat Vehicles (Cars / Bikes)" helperText="Add cars & bikes for this flat." />
                    </div>
                    <select value={form.occupancy} onChange={(e) => setForm({ ...form, occupancy: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="flat-occupancy">
                        <option value="owner">Owner-occupied</option>
                        <option value="tenant">Rented / Tenant</option>
                        <option value="vacant">Vacant</option>
                    </select>
                    <textarea rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-transparent border border-nest-line rounded-md focus:border-nest-clay outline-none p-3" data-testid="flat-notes"/>
                    <button type="submit" disabled={saving} className="nest-btn-primary mt-2" data-testid="flat-save">{saving ? "Saving…" : (initial?.flat_id ? "Save changes" : "Add flat")}</button>
                </form>
            </div>
        </div>
    );
}

function ResidentEditModal({ open, resident, onClose, onSaved, onDelete }) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [tower, setTower] = useState("");
    const [flatNo, setFlatNo] = useState("");
    const [vehicles, setVehicles] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (resident) {
            setName(resident.name || "");
            setPhone(resident.phone || "");
            setTower(resident.tower || "");
            setFlatNo(resident.flat_no || "");
            setVehicles(parseVehicles(resident.vehicles));
        }
    }, [resident]);

    if (!open || !resident) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/admin/residents/${resident.user_id || resident.id}`, {
                name: name.trim(),
                phone: phone.trim(),
                tower: tower.trim().toUpperCase(),
                flat_no: flatNo.trim(),
                vehicles: formatVehiclesForPayload(vehicles),
            });
            toast.success("Resident details updated");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update resident");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl border border-nest-line w-full max-w-lg p-6 lg:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="resident-edit-modal">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="nest-stat-label">Admin Management</p>
                        <h3 className="font-display text-2xl mt-1 tracking-tight">Edit Resident Flat & Info</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-nest-alt"><X size={16}/></button>
                </div>
                <div className="mt-4 p-3 bg-nest-alt/60 rounded-xl flex items-center gap-3">
                    {resident.picture ? (
                        <img src={resident.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                        <div className="h-10 w-10 rounded-full bg-nest-sand grid place-items-center font-display">{resident.name?.[0]}</div>
                    )}
                    <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{resident.name}</p>
                        <p className="text-xs text-nest-ink3 truncate">{resident.email}</p>
                    </div>
                </div>
                <form onSubmit={submit} className="mt-5 grid gap-4">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 block mb-1">Resident Name</label>
                        <input required placeholder="Resident name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2 text-sm" data-testid="edit-res-name"/>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 block mb-1">Tower</label>
                            <input required placeholder="Tower" value={tower} onChange={(e) => setTower(e.target.value)} className="w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2 text-sm uppercase" data-testid="edit-res-tower"/>
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 block mb-1">Flat No.</label>
                            <input required placeholder="Flat no." value={flatNo} onChange={(e) => setFlatNo(e.target.value)} className="w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2 text-sm" data-testid="edit-res-flat"/>
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-widest text-nest-ink2 block mb-1">Phone Number</label>
                        <input placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2 text-sm" data-testid="edit-res-phone"/>
                    </div>
                    <div className="pt-1">
                        <VehiclesManager vehicles={vehicles} onChange={setVehicles} label="Cars / Bikes Numbers" helperText="Add cars & bikes for gate security." />
                    </div>
                    <div className="pt-3 flex flex-wrap items-center justify-between gap-2 border-t border-nest-line mt-3">
                        {onDelete ? (
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    onDelete(resident);
                                }}
                                className="px-3 py-2 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs sm:text-sm font-bold flex items-center gap-1.5 transition cursor-pointer"
                                title="Permanently remove resident who left society"
                                data-testid="edit-res-delete-btn"
                            >
                                <Trash2 size={14} />
                                <span>Remove Resident (Left Society)</span>
                            </button>
                        ) : <div />}
                        <div className="flex items-center gap-2 ml-auto">
                            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-nest-line text-sm hover:bg-nest-alt transition cursor-pointer">Cancel</button>
                            <button type="submit" disabled={saving} className="nest-btn-primary" data-testid="edit-res-save">{saving ? "Saving…" : "Save Changes"}</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function Directory() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isSuperAdmin = (user?.email || "").toLowerCase() === "aws.tilak007@gmail.com";
    const [activeTab, setActiveTab] = useState("residents");
    const [items, setItems] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [q, setQ] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editingResident, setEditingResident] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get("tab");
        if (t && ["residents", "pending", "guards", "test-admins"].includes(t)) {
            setActiveTab(t);
        }
    }, []);

    const load = async () => {
        try {
            const r = await api.get("/directory");
            setItems(r.data);
            if (isAdmin) {
                const p = await api.get("/admin/pending-residents");
                setPendingCount(p.data.length);
            }
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const filtered = items.filter((u) => {
        if (!q.trim()) return true;
        const t = q.toLowerCase();
        const vStr = vehicleSearchString(u.vehicles);
        return [u.name, u.first_name, u.last_name, u.owner_name, u.tenant_name, u.email, u.tower, u.flat_no, u.phone, vStr].some((v) => (v || "").toString().toLowerCase().includes(t));
    });

    const del = async (id) => {
        if (!window.confirm("Remove this flat record?")) return;
        try { await api.delete(`/flats/${id}`); setItems((arr) => arr.filter((x) => x.id !== id)); toast.success("Removed"); }
        catch { toast.error("Failed"); }
    };

    const delResident = async (u) => {
        const dName = (u.first_name || u.last_name) ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : (u.name || "this resident");
        const flatStr = u.tower && u.flat_no ? `${u.tower}-${u.flat_no}` : (u.flat_no || "their flat");
        const confirmMsg = `Are you sure you want to permanently remove resident ${dName} (Flat ${flatStr}) from the society?\n\nThey will be unassigned from Flat ${flatStr}, removed from the directory, and their society access cleared.`;
        if (!window.confirm(confirmMsg)) return;
        try {
            await api.delete(`/admin/residents/${u.user_id || u.id}`);
            toast.success(`✓ Resident ${dName} permanently removed from society`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to remove resident");
        }
    };

    return (
        <div className="animate-fade-up" data-testid="directory-page">
            {isAdmin && (
                <div className="flex items-center gap-2 border-b border-nest-line mb-6 sm:mb-8 overflow-x-auto no-scrollbar pb-1">
                    <button
                        onClick={() => setActiveTab("residents")}
                        className={`pb-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold transition border-b-2 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap shrink-0 ${
                            activeTab === "residents"
                                ? "border-nest-clay text-nest-ink"
                                : "border-transparent text-nest-ink3 hover:text-nest-ink"
                        }`}
                        data-testid="tab-residents"
                    >
                        <Users size={15} /> Flats & Residents ({items.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("pending")}
                        className={`pb-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold transition border-b-2 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap shrink-0 ${
                            activeTab === "pending"
                                ? "border-nest-clay text-nest-ink"
                                : "border-transparent text-nest-ink3 hover:text-nest-ink"
                        }`}
                        data-testid="tab-pending"
                    >
                        <UserCheck size={15} /> Pending Approvals
                        {pendingCount > 0 && (
                            <span className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-bold">
                                {pendingCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab("guards")}
                        className={`pb-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold transition border-b-2 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap shrink-0 ${
                            activeTab === "guards"
                                ? "border-nest-clay text-nest-ink"
                                : "border-transparent text-nest-ink3 hover:text-nest-ink"
                        }`}
                        data-testid="tab-guards"
                    >
                        <Shield size={15} /> Security Guards
                    </button>
                    {isSuperAdmin && (
                        <button
                            onClick={() => setActiveTab("test-admins")}
                            className={`pb-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold transition border-b-2 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap shrink-0 ${
                                activeTab === "test-admins"
                                    ? "border-amber-600 text-nest-ink font-bold"
                                    : "border-transparent text-nest-ink3 hover:text-nest-ink"
                            }`}
                            data-testid="tab-test-admins"
                        >
                            <KeyRound size={15} className="text-amber-600" /> Test Admins
                            <span className="bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-1.5 py-0.2 text-[9px] font-bold uppercase tracking-wider">
                                Super Admin
                            </span>
                        </button>
                    )}
                </div>
            )}

            {activeTab === "pending" && isAdmin ? (
                <PendingResidentsManager onCountChange={setPendingCount} />
            ) : activeTab === "guards" && isAdmin ? (
                <GuardsManager />
            ) : activeTab === "test-admins" && isSuperAdmin ? (
                <TestAdminsManager />
            ) : (
                <>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
                        <div>
                            <p className="nest-stat-label">Residents</p>
                            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 sm:mt-2">Neighbour directory</h1>
                            <p className="text-nest-ink2 text-sm sm:text-base mt-1 sm:mt-2">Registered residents + flats added by admin.</p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full md:w-auto">
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, flat, or vehicle..." className="bg-white border border-nest-line rounded-xl px-4 py-2.5 w-full md:w-72 outline-none focus:border-nest-clay text-sm shadow-xs" data-testid="directory-search"/>
                            {isAdmin && (
                                <button onClick={() => { setEditing(null); setShowForm(true); }} className="nest-btn-primary flex items-center justify-center gap-2 text-sm py-2.5 whitespace-nowrap" data-testid="add-flat-btn"><Plus size={16}/> Add flat</button>
                            )}
                        </div>
                    </div>

            <div className="mt-6 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-5">
                {filtered.length === 0 && <div className="col-span-full nest-card p-10 text-center text-nest-ink2" data-testid="no-directory">No matching entries.</div>}
                {filtered.map((u) => {
                    const vList = parseVehicles(u.vehicles);
                    const displayName = (u.first_name || u.last_name) ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : (u.name || "");
                    return (
                    <div key={u.id} className="nest-card p-5 flex items-start gap-4" data-testid={`directory-${u.id}`}>
                        {u.kind === "registered" ? (
                            u.picture ? <img src={u.picture} alt="" className="h-12 w-12 rounded-full object-cover"/> : <div className="h-12 w-12 rounded-full bg-nest-sand grid place-items-center font-display text-nest-ink font-semibold">{displayName?.[0] || "R"}</div>
                        ) : (
                            <div className="h-12 w-12 rounded-lg bg-nest-alt grid place-items-center font-display text-nest-ink text-sm">{u.tower}-{u.flat_no}</div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                {u.kind === "registered" ? (
                                    <>
                                        <p className="font-medium truncate">{displayName}</p>
                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-nest-sage"><BadgeCheck size={11}/> Registered</span>
                                        {u.occupancy && (
                                            <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                                                u.occupancy === "tenant" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                            }`}>
                                                {u.occupancy}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="font-medium truncate">{u.owner_name || u.tenant_name || `Flat ${u.tower}-${u.flat_no}`}</p>
                                        <span className="text-[9px] font-bold uppercase tracking-widest text-nest-ink3">{u.occupancy}</span>
                                    </>
                                )}
                            </div>
                            {u.kind === "flat" && u.owner_name && u.tenant_name && (
                                <p className="text-xs text-nest-ink2 mt-0.5">Owner: {u.owner_name} · Tenant: {u.tenant_name}</p>
                            )}
                            <p className="text-xs text-nest-ink3 truncate mt-0.5">{u.tower}-{u.flat_no}{(u.phone || u.email) ? ` · ${u.phone || u.email}` : ""}</p>
                            {vList.length > 0 && (
                                <div className="flex items-center gap-1.5 mt-2 text-xs flex-wrap" title="Registered vehicles">
                                    {vList.map((v, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1 font-mono text-[11px] bg-nest-alt px-2 py-0.5 rounded-md border border-nest-line text-nest-ink"
                                        >
                                            {v.type === "bike" ? (
                                                <Bike size={12} className="text-nest-clay flex-shrink-0" />
                                            ) : (
                                                <Car size={12} className="text-nest-clay flex-shrink-0" />
                                            )}
                                            <span>{v.number}</span>
                                        </span>
                                    ))}
                                </div>
                            )}
                            {u.notes && <p className="text-xs text-nest-ink3 mt-1.5 italic">{u.notes}</p>}
                        </div>
                        {isAdmin && (
                            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 shrink-0">
                                {u.kind === "registered" ? (
                                    <>
                                        <button
                                            onClick={() => setEditingResident(u)}
                                            className="px-2.5 py-1.5 rounded-xl border border-nest-line bg-nest-card hover:bg-nest-alt text-nest-ink transition flex items-center gap-1 text-xs font-semibold cursor-pointer"
                                            title="Edit resident flat assignment"
                                            data-testid={`res-edit-${u.id}`}
                                        >
                                            <Pencil size={12}/>
                                            <span>Edit</span>
                                        </button>
                                        <button
                                            onClick={() => delResident(u)}
                                            className="px-2.5 py-1.5 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 transition flex items-center gap-1 text-xs font-semibold cursor-pointer shadow-2xs"
                                            title="Permanently remove resident who left society"
                                            data-testid={`res-delete-${u.id}`}
                                        >
                                            <Trash2 size={12}/>
                                            <span>Remove</span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => { setEditing(u); setShowForm(true); }}
                                            className="px-2.5 py-1.5 rounded-xl border border-nest-line bg-nest-card hover:bg-nest-alt text-nest-ink transition flex items-center gap-1 text-xs font-semibold cursor-pointer"
                                            title="Edit flat record"
                                            data-testid={`flat-edit-${u.id}`}
                                        >
                                            <Pencil size={12}/>
                                            <span>Edit</span>
                                        </button>
                                        <button
                                            onClick={() => del(u.flat_id)}
                                            className="px-2.5 py-1.5 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 transition flex items-center gap-1 text-xs font-semibold cursor-pointer shadow-2xs"
                                            title="Delete flat record"
                                            data-testid={`flat-delete-${u.id}`}
                                        >
                                            <Trash2 size={12}/>
                                            <span>Remove</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>

            <FlatForm open={showForm} initial={editing} onClose={() => setShowForm(false)} onSaved={() => load()} />
            <ResidentEditModal open={Boolean(editingResident)} resident={editingResident} onClose={() => setEditingResident(null)} onSaved={() => load()} onDelete={delResident} />
            </>
            )}
        </div>
    );
}
